import { createTranslationHook } from '../i18nFactory';
import { TEXTO_COMUN_ES, TEXTO_COMUN_EN } from '../commonTexts';
import type { TranslateFn } from '../i18nFactory';

/**
 * i18n del widget de Anticipos.
 *
 * Cumple CA-F8 (usa `useTranslation` de siesa-ui-kit) y CA-F9 (paridad ES/EN).
 * El motor de traducción del kit hace fallback al `key` cuando el host no ha
 * registrado las claves `advances.*`; por eso este módulo provee diccionarios
 * locales que se usan como respaldo según el idioma activo, garantizando texto
 * listo para usar sin depender de que el documento consumidor registre los recursos.
 *
 * Las claves siguen el formato plano (no anidado) que espera
 * `DefaultTranslationEngine`.
 */

export const ADVANCES_TRANSLATIONS_ES: Record<string, string> = {
  'advances.panel.title': 'Anticipos aplicados',
  'advances.panel.empty': 'No hay anticipos aplicados a este documento.',
  'advances.panel.apply': 'Aplicar anticipo',
  'advances.panel.remove': 'Quitar',
  'advances.panel.removeAria': 'Quitar anticipo aplicado',
  'advances.panel.available_one': '{count} saldo disponible',
  'advances.panel.available_other': '{count} saldos disponibles',
  'advances.panel.loading': 'Cargando anticipos…',
  'advances.panel.loadError': 'No se pudieron cargar los anticipos.',
  'advances.status.reserved': 'Reservado',
  'advances.status.confirmed': 'Confirmado',
  'advances.status.reversed': 'Reversado',
  'advances.modal.title': 'Aplicar anticipo',
  'advances.modal.balanceLabel': 'Saldo de anticipo',
  'advances.modal.balancePlaceholder': 'Selecciona un saldo de anticipo',
  'advances.modal.amountLabel': 'Monto a aplicar',
  'advances.modal.availableLabel': 'Disponible',
  'advances.modal.cancel': TEXTO_COMUN_ES.cancel,
  'advances.modal.confirm': 'Aplicar anticipo',
  'advances.modal.applying': 'Aplicando…',
  'advances.error.amountPositive': 'El monto debe ser mayor a 0.',
  'advances.error.amountExceeds': 'El monto supera el disponible del saldo.',
  'advances.error.selectBalance': 'Selecciona un saldo de anticipo.',
  'advances.error.generic': TEXTO_COMUN_ES.errorGeneric,
  'advances.error.forbidden': TEXTO_COMUN_ES.errorForbidden,
  'advances.toast.applied': 'Anticipo aplicado correctamente.',
  'advances.toast.removed': 'Anticipo quitado correctamente.',
};

export const ADVANCES_TRANSLATIONS_EN: Record<string, string> = {
  'advances.panel.title': 'Applied advances',
  'advances.panel.empty': 'No advances applied to this document.',
  'advances.panel.apply': 'Apply advance',
  'advances.panel.remove': 'Remove',
  'advances.panel.removeAria': 'Remove applied advance',
  'advances.panel.available_one': '{count} available balance',
  'advances.panel.available_other': '{count} available balances',
  'advances.panel.loading': 'Loading advances…',
  'advances.panel.loadError': 'Could not load advances.',
  'advances.status.reserved': 'Reserved',
  'advances.status.confirmed': 'Confirmed',
  'advances.status.reversed': 'Reversed',
  'advances.modal.title': 'Apply advance',
  'advances.modal.balanceLabel': 'Advance balance',
  'advances.modal.balancePlaceholder': 'Select an advance balance',
  'advances.modal.amountLabel': 'Amount to apply',
  'advances.modal.availableLabel': 'Available',
  'advances.modal.cancel': TEXTO_COMUN_EN.cancel,
  'advances.modal.confirm': 'Apply advance',
  'advances.modal.applying': 'Applying…',
  'advances.error.amountPositive': 'The amount must be greater than 0.',
  'advances.error.amountExceeds': 'The amount exceeds the balance available.',
  'advances.error.selectBalance': 'Select an advance balance.',
  'advances.error.generic': TEXTO_COMUN_EN.errorGeneric,
  'advances.error.forbidden': TEXTO_COMUN_EN.errorForbidden,
  'advances.toast.applied': 'Advance applied successfully.',
  'advances.toast.removed': 'Advance removed successfully.',
};

/** Diccionarios de respaldo por idioma, listos para registrar en el host si se desea. */
export const ADVANCES_TRANSLATIONS = {
  es: ADVANCES_TRANSLATIONS_ES,
  en: ADVANCES_TRANSLATIONS_EN,
} as const;

/** Función de traducción con interpolación `{var}`. */
export type AdvancesT = TranslateFn;

/**
 * Hook de traducción del widget.
 *
 * Usa `useTranslation` del kit; si la clave no está registrada en el host
 * (el motor devuelve la propia clave), cae al diccionario local del idioma activo.
 */
export const useAdvancesTranslation = createTranslationHook(ADVANCES_TRANSLATIONS);
